import { type IReference, ReferenceType } from '../references/IReference.ts';
import { ReferencePathDirection } from '../references/IReferencePath.ts';
import { mapReferenceToPath } from './mapReferenceToPath.ts';

/**
 * Riferimento materializzato, ovvero contiene anche il path assoluto.
 * Utilizzato lato React (es. `useWatch`), mentre lato Yup/validatore
 * è sufficiente `mapReferenceToPath` che è _relativo_
 */
export interface IMaterializedReference extends IReference {
  absolutePath: string;
}

export function materializeReference(
  reference: IReference,
  basePath: string,
): IMaterializedReference {
  return {
    ...reference,
    absolutePath: materializeReferencePath(basePath, reference),
  };
}

export function materializeReferences(
  references: IReference[],
  basePath: string,
): IMaterializedReference[] {
  return references.map(r => materializeReference(r, basePath));
}

/**
 * Ottiene il path completo di una reference a partire dal basePath, passando
 * da un path relativo a uno assoluto.
 * @param basePath Path diviso da punti dalla root all'elemento preso in
 *                 considerazione
 */
export function materializeReferencePath(
  basePath: string,
  reference: IReference,
) {
  const basePathTokens = basePathToTokens(basePath);
  return mapReferenceToAbsolutePath(reference, basePathTokens);
}

/**
 * Data una reference, calcola il path completo a partire dalla root.
 *
 * Per i Widget processa direttamente `reference.path` senza passare dalla
 * rappresentazione stringa intermedia (`mapReferenceToPath`), evitando
 * allocazioni di array (split, flatMap, slice) nel hot-path.
 *
 * Per i tipi non-Widget ($session, $sample, ecc.) delega a
 * `mapReferenceToPath` che ritorna path assoluti.
 *
 * @param basePathTokens Array di token del path dalla root all'elemento preso
 *                       in considerazione. Non deve contenere il nome del campo.
 */
export function mapReferenceToAbsolutePath(
  reference: IReference,
  basePathTokens: string[] = [],
) {
  // Non-Widget: path assoluti ($session, $sample, ecc.)
  if (reference.type !== ReferenceType.Widget) {
    return mapReferenceToPath(reference);
  }

  const tokens = [...basePathTokens];
  const path = reference.path;

  // Processiamo direttamente gli item del path della reference.
  //
  // Importante: con la dot notation di RHF una riga repeatable viene
  // serializzata come `lista.3.campo` dove `3` e `lista` sono token
  // separati ma rappresentano lo stesso scope logico. Per questo, quando
  // risaliamo (Parent o Root), poppiamo anche l'eventuale indice numerico.
  for (const item of path) {
    switch (item.direction) {
      case ReferencePathDirection.Parent:
        popParentValueNode(tokens, item.code);
        break;

      case ReferencePathDirection.Root:
        // Esce dallo scope corrente e entra nella nuova sezione.
        // Le sezioni non sono mai array, quindi semplice pop.
        tokens.pop();
        tokens.push(item.code);
        break;

      default:
        if (item.code) tokens.push(item.code);
        break;
    }
  }

  tokens.push(reference.code);
  return tokens.join('.');
}

/**
 * Risale di un nodo valore nell'array di token.
 *
 * Con la dot notation di RHF un indice array (es. `3`) è un token separato
 * dal nome del parent (es. `lista_rip.3`). Se l'ultimo token è un indice
 * numerico lo poppiamo sempre — corrisponde al vecchio comportamento dove
 * `lista_rip[3]` era un token unico e un singolo `pop()` li toglieva
 * entrambi.
 *
 * `expectedParentCode` è usato solo per diagnostica: se presente e non
 * corrisponde al token che precede l'indice, logghiamo un warning.
 */
function popParentValueNode(
  tokens: string[],
  expectedParentCode: string | undefined,
) {
  if (isArrayIndexToken(tokens.at(-1))) {
    if (expectedParentCode && tokens.at(-2) !== expectedParentCode) {
      console.warn(
        '[mapReferenceToAbsolutePath] Parent code mismatch:',
        `expected '${expectedParentCode}',`,
        `found '${tokens.at(-2)}' before index '${tokens.at(-1)}'.`,
      );
    }
    tokens.pop();
  }

  tokens.pop();
}

// biome-ignore lint/style/noNonNullAssertion: costanti statiche, codePointAt su stringa non vuota non è mai undefined
const ZERO = '0'.codePointAt(0)!;
// biome-ignore lint/style/noNonNullAssertion: costanti statiche
const NINE = '9'.codePointAt(0)!;

function isArrayIndexToken(token: string | undefined): boolean {
  if (token == null || token.length === 0) return false;
  for (let i = 0; i < token.length; i++) {
    const c = token.codePointAt(i);
    if (c === undefined || c < ZERO || c > NINE) return false;
  }
  return true;
}

/**
 * Converte un basePath in un array di token, rimuovendo l'estremo che è il nome del campo.
 */
function basePathToTokens(basePath: string) {
  const tokens = basePath.split('.');
  // Rimuovo l'estremo, che è il nome del campo
  tokens.pop();
  return tokens;
}
